import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import type { CameraZone } from '../types/camera-zones';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import type { Device } from 'src/types/Device';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';

// Zone color palette
export const CAMERA_ZONES_COLORS: CameraZone['color'][] = [
	[248, 55, 27], // #F8371B - Main/Zone1 (red)
	[119, 92, 255], // #775CFF - Zone2 (purple)
	[46, 174, 248], // #2EAEF8 - Zone3 (blue)
	[65, 209, 180], // #41D1B4 - Zone4 (green/teal)
	[249, 202, 31], // #F9CA1F - Zone5 (yellow)
	[246, 123, 28], // #F67B1C - Zone6 (orange)
	[249, 54, 138], // #F9368A - Zone7 (pink)
	[0, 84, 255], // #0054FF - Zone8 (dark blue)
	[13, 131, 76], // #0D834C - Zone9 (dark green)
];

export const isDefaultZone = (zone_id: number): boolean => {
	return zone_id === 200;
};

export const getCameraZonesList = (device_settings: DeviceSettingsG2) => {
	let zonesComponents = [];
	if (!device_settings) {
		return [];
	}

	zonesComponents = Object.keys(device_settings)
		.filter((key) => key.startsWith('camerazone:'))
		.sort();
	return zonesComponents;
};

export const getCameraZones = (device_settings: DeviceSettingsG2): CameraZone[] => {
	if (!device_settings) {
		return [];
	}

	return Object.keys(device_settings)
		.filter((key) => key.startsWith('camerazone:'))
		.map((component) => device_settings[component]);
};

export const getCameraZoneData = (
	device_status: DeviceStatusG2,
	device_settings: DeviceSettingsG2,
): any => {
	if (!device_status || !device_settings) {
		return [];
	}

	return Object.keys(device_status)
		.filter((key) => key.startsWith('camerazone:'))
		.map((component) => {
			return {
				component,
				motion: device_status[component]?.motion || false,
				name: device_settings[component]?.name || '',
				color: device_settings[component]?.color || CAMERA_ZONES_COLORS[0],
			};
		})
		.sort((a, b) => a.name.localeCompare(b.name));
};

export const getZoneId = (zone_component: string): number => {
	const parts = zone_component?.split(':') || ['', '0'];
	return Number(parts[1]);
};

export const getCameraZone = (device_settings: DeviceSettingsG2, zone_id: number): CameraZone | null => {
	if (!device_settings) {
		return null;
	}

	const component = `camerazone:${zone_id}`;
	return device_settings?.[component];
};

export const addCameraZone = async (device: Device, zone: CameraZone): Promise<void> => {
	let channel = device?.channel || 0;
	const res: SettingRequest = await gen2Request(device, 'Camera.AddZone', {
		id: channel,
		name: zone.name,
		color: zone.color,
		coordinates: zone.coordinates,
		enable: zone.enable,
		type: zone.type,
	});

	return res;
};

export const enableCameraZone = async (
	device: Device,
	zone_id: number,
	enable: boolean,
): Promise<void> => {
	let config: Partial<CameraZone> = {
		enable,
	};
	const res: SettingRequest = await gen2Request(device, 'CameraZone.SetConfig', {
		id: zone_id,
		config,
	});

	return res;
};

export const updateCameraZone = async (
	device: Device,
	zone_id: number,
	zone: CameraZone,
): Promise<void> => {
	let config: CameraZone = {
		name: zone.name,
		color: zone.color,
		coordinates: zone.coordinates,
		enable: zone.enable,
	};
	const res: SettingRequest = await gen2Request(device, 'CameraZone.SetConfig', {
		id: zone_id,
		config,
	});

	return res;
};

export const deleteCameraZone = async (
	device: Device,

	zone_id: number,
): Promise<void> => {
	let channel = device?.channel || 0;
	const res: SettingRequest = await gen2Request(device, 'Camera.DeleteZone', {
		id: channel,
		zone_id,
	});

	return res;
};
